
































































import {Component, Inject, Prop, Watch, Provide, Mixins} from 'vue-property-decorator';
import {WorkloadWidget} from '@v/dashboard/interfaces';
import {requestWorkloadData} from '@api/services/workloadApi';
import {statusDiagramColorScheme as colorScheme} from '@c/system/configurate';
import {DateTime} from 'luxon';
import {Chart} from 'highcharts-vue';
import WidgetBindDataSource from '@/components/widgets/base/WidgetBindDataSource.vue';
import Loading from '@c/controls/Loading.vue';
import WidgetDataTimerMixin from '@/mixins/WidgetDataTimerMixin';

@Component({
  components: {
    highcharts: Chart,
    WidgetBindDataSource,
    Loading,
  },
})
export default class WorkloadWidgetComponent extends Mixins(WidgetDataTimerMixin) {
  @Provide()
  @Inject()
  widget!: WorkloadWidget;

  //данные для легенды
  legendInfo = [
    {color: colorScheme.work, name: 'Рез'},
    {color: colorScheme.idle, name: 'XX'},
    {color: colorScheme.off, name: 'Выкл'},
    {color: colorScheme.incorrect, name: 'СО'},
  ];

  // Период обновления данных (секунд)
  protected getDataTimeout = 86400;

  //цвета для фона
  colorsBgInterval = [colorScheme.off, colorScheme.idle, colorScheme.work, colorScheme.incorrect];

  // Цвет заголовка
  colorCaption = '#e5e5e5';

  states: any[] = [];

  // количество месяцев без текущего месяца
  amountMonths = 3;

  heightChart = 94;

  //показатели загрузки данных и наличия ошибок получения данных с сервера
  workloadLoading = true;
  workloadError = false;

  //Месяцы для заголовка диаграммы
  months!: string[];

  //Идентификатор узла (т. к. с использованием widget.nodeId темплейт не перерисовывается)
  nodeId: string | null = null;

  // Наименование узла
  nodeName = '';

  // TODO когда в БД появятся краткие наименования заменить
  get nodeShortName() {
    return this.nodeName;
  }

  //Объект графика
  getChart() {
    return (this.$refs.chart as any)?.chart;
  }

  @Prop({required: false})
  colNum!: number;

  @Watch('colNum')
  onAmountColChanged() {
    this.getChart()?.reflow();
  }

  // Действия при выборе узла
  onNodeSelect(data: {nodeId: string; nodeName: string}) {
    this.widget.nodeId = data.nodeId;
    this.widget.nodeName = data.nodeName;
    this.nodeId = data.nodeId;
    this.nodeName = data.nodeName;
    this.attachNode(this.widget.nodeId);
  }

  created() {
    this.nodeId = this.widget.nodeId;
    this.nodeName = this.widget.nodeName;
    this.months = this.getMonths();
    this.attachNode(this.widget.nodeId);
  }

  // Привязка узла
  attachNode(nodeId: string | null) {
    if (!nodeId) return;
    this.clearRefresh();
    this.workloadLoading = true;
    this.refresh(async () => {
      await this.getData(nodeId);
    });
  }

  mounted() {
    //хак для обновления диаграммы, после того как станут известны размеры виджета
    this.$nextTick(() => {
      this.getChart()?.reflow();
    });
  }

  getStartDate() {
    const firstDayOfThisMonth = DateTime.local().startOf('month');
    return firstDayOfThisMonth.minus({months: this.amountMonths}).valueOf();
  }

  async getAggregationData(array: any[], startDate: number) {
    const res = [...Array(4)].map(() => Array(this.amountMonths + 1).fill(0));

    const all = Array(this.amountMonths + 1).fill(0);
    const firstMonth = DateTime.fromMillis(startDate).month;

    array.forEach((elem: any) => {
      const startMonth = DateTime.fromMillis(elem.start).month;
      const endMonth = DateTime.fromMillis(elem.end).month;

      if (startMonth === endMonth) {
        res[elem.mode][startMonth - firstMonth] += elem.end - elem.start;
        all[startMonth - firstMonth] += elem.end - elem.start;
      } else {
        const bound = DateTime.fromMillis(elem.end)
          .startOf('month')
          .valueOf();
        res[elem.mode][startMonth - firstMonth] += bound - elem.start;
        all[startMonth - firstMonth] += bound - elem.start;
        res[elem.mode][endMonth - firstMonth] += elem.end - bound;
        all[endMonth - firstMonth] += elem.end - bound;
      }
    });

    const resInPercent = res.map((row: any) => {
      return row.map((elem: any, index: number) => {
        return (elem / all[index]) * 100;
      });
    });

    return resInPercent;
  }

  async getData(nodeId: string) {
    const startDate = this.getStartDate();
    const endDate = Date.now();
    this.workloadError = false;

    requestWorkloadData(nodeId, startDate, endDate)
      .then(async (res: any) => {
        const {intervalsModes} = res;
        this.states = await this.getAggregationData(intervalsModes, startDate);
      })
      .catch(() => {
        this.workloadError = true;
      })
      .finally(() => {
        this.workloadLoading = false;
      });
  }

  //Получение месяцев для заголовка диаграммы
  getMonths() {
    const months: string[] = [];
    const now = DateTime.local();
    for (let i = this.amountMonths; i >= 0; i--) {
      const month = now.minus({months: i}).setLocale('ru').monthLong;
      months.push(month.slice(0, 3));
    }
    return months;
  }

  get chartOptions() {
    const series: any[] = [];

    this.states.forEach((s: any, index: number) => {
      series.push({
        type: 'column',
        color: this.colorsBgInterval[index],
        data: s,
      });
    });

    return {
      credits: {
        enabled: false,
      },
      chart: {
        height: this.heightChart,
        marginLeft: 0,
        marginRight: 0,
        spacingLeft: 0,
        spacingRight: 0,
        marginTop: 0,
        marginBottom: 0,
        spacingTop: 0,
        spacingBottom: 0,
        animation: false,
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      plotOptions: {
        column: {
          enableMouseTracking: false,
          animation: false,
        },
        series: {
          stacking: 'percent',
          groupPadding: 0,
          pointPadding: 0,
          borderWidth: 1,
          borderColor: 'black',
        },
      },
      tooltip: {
        enabled: false,
      },
      title: {
        text: '',
      },
      legend: {
        enabled: false,
      },
      series,
    };
  }
}
